import { configureStore as configureReduxStore } from "@reduxjs/toolkit";
import { combineReducers } from "redux";
import type { IAccountModel } from "./account/account-reducer";
import { accountReducer } from "./account/account-reducer";
import type { IEarningsModel } from "./account/earnings/earnings-reducer";
import { earningsReducer } from "./account/earnings/earnings-reducer";
import { api } from "./api";
import type { IBackgroundUploaderModel } from "./background-uploader/background-uploader-reducer";
import { backgroundUploaderReducer } from "./background-uploader/background-uploader-reducer";
import type { IChatsModel } from "./chats/chats-reducer";
import { chatsReducer } from "./chats/chats-reducer";
import type { IMessengerModel } from "./chats/messenger/messenger-reducer";
import { messengerReducer } from "./chats/messenger/messenger-reducer";
import type { IDiscoverModel } from "./discover/discover-reducer";
import { discoverReducer } from "./discover/discover-reducer";
import type { IModelProfileModel } from "./discover/model-profile/model-profile-reducer";
import { modelProfileReducer } from "./discover/model-profile/model-profile-reducer";
import type { IFeedModel } from "./feed/feed-reducer";
import { feedReducer } from "./feed/feed-reducer";
import type { ILandingModel } from "./landing/landing-reducer";
import { landingReducer } from "./landing/landing-reducer";
import type { IPubnubModel } from "./pubnub/pubnub-reducer";
import { pubnubReducer } from "./pubnub/pubnub-reducer";
import type { IWebsocketModel } from "./websocket/websocket-reducer";
import { websocketReducer } from "./websocket/websocket-reducer";

// Dynamically imported (not a static top-level import) so redux-logger's
// code is excluded from the production bundle entirely, matching the old
// CRA/webpack build's inline require() - a static import would get bundled
// even though it's never called, since Rollup doesn't eliminate this
// runtime-only conditional.
const devLogger = import.meta.env.DEV
  ? (await import("redux-logger")).createLogger
  : undefined;

export interface RootState {
  landingStore: ILandingModel;
  discoverStore: IDiscoverModel;
  [api.reducerPath]: ReturnType<typeof api.reducer>;
  accountStore: IAccountModel;
  earningsStore: IEarningsModel;
  feedStore: IFeedModel;
  chatsStore: IChatsModel;
  messengerStore: IMessengerModel;
  modelProfileStore: IModelProfileModel;
  pubnubStore: IPubnubModel;
  backgroundUploaderStore: IBackgroundUploaderModel;
  websocketStore: IWebsocketModel;
}

export function configureStore(initialState?: RootState) {
  const rootReducer = combineReducers({
    landingStore: landingReducer,
    discoverStore: discoverReducer,
    [api.reducerPath]: api.reducer,
    accountStore: accountReducer,
    earningsStore: earningsReducer,
    feedStore: feedReducer,
    chatsStore: chatsReducer,
    messengerStore: messengerReducer,
    modelProfileStore: modelProfileReducer,
    pubnubStore: pubnubReducer,
    backgroundUploaderStore: backgroundUploaderReducer,
    websocketStore: websocketReducer,
  });

  const store = configureReduxStore({
    reducer: rootReducer,
    preloadedState: initialState,
    devTools: import.meta.env.DEV,
    middleware: (getDefaultMiddleware) => {
      // `thunk` middleware must stay enabled: an RTK Query endpoint's
      // `initiate()` is itself a thunk, so the shared API slice needs it
      // to work at all. Every other action creator in this app dispatches
      // a plain object directly, so this doesn't change how those behave.
      const middleware = getDefaultMiddleware({
        actionCreatorCheck: false,
        immutableCheck: false,
        serializableCheck: false,
      }).concat(api.middleware);

      if (devLogger) {
        return middleware.concat(devLogger({ collapsed: true }));
      }

      return middleware;
    },
  });

  return store;
}

// Typed alternatives to plain `Store`/`Dispatch` from `redux`, which don't
// know about the thunk middleware RTK Query's `initiate()` relies on. Used by
// `useAppDispatch` (src/hooks.ts) so every call site - not just ones that
// happen to dispatch an RTK Query thunk - gets this instead of plain
// `Dispatch` or a cast to `any`.
type AppStore = ReturnType<typeof configureStore>;
export type AppDispatch = AppStore["dispatch"];
